import { useStripe, useElements, Elements } from '@stripe/react-stripe-js'
import { type Stripe, loadStripe, type StripeElements } from '@stripe/stripe-js'
import {
  createContext,
  useContext,
  useState,
  useEffect,
  type ReactNode,
} from 'react'
import { createReturnUrl } from '#app/features/payment/createReturnUrl.js'
import { PaymentEnum } from '../../../../../../types/enums'
import usePaymentIntentAPIs, {
  type PaymentIntentData,
} from './usePaymentIntentAPIs'

type ElementsContextProps = {
  isElementsReady: boolean
  stripe: Stripe | null
  elements: StripeElements | null
  message: string | null | undefined
  isLoading: boolean
  handleStripeFromSubmit?: (fomData: FormData) => Promise<void>
}

export const ElementsContext = createContext<ElementsContextProps>({} as any)

export const useElementsContext = () => {
  const context = useContext(ElementsContext)
  if (!context) {
    throw new Error(
      'useElementsContext must be rendered within the SocketProvider',
    )
  }
  return context
}

type ElementProviderProps = {
  stripePublicKey: string
  amount: number
  purchaseDescription: string
  baseUrl: string
  locale: string
  children: ReactNode
}

export function ElementProvider({
  stripePublicKey,
  amount,
  purchaseDescription,
  baseUrl,
  locale,
  children,
}: ElementProviderProps) {
  const { paymentIntent } = usePaymentIntentAPIs({
    amount,
    purchaseDescription,
  })

  if (!paymentIntent.secret) {
    // until we don't have the secret, we don't render the elements
    return (
      <ElementsContext.Provider
        value={{
          isElementsReady: false,
          stripe: null,
          elements: null,
          message: null,
          isLoading: false,
          handleStripeFromSubmit: undefined,
        }}
      >
        {children}
      </ElementsContext.Provider>
    )
  }

  return (
    <Elements
      stripe={loadStripe(stripePublicKey)}
      options={{
        clientSecret: paymentIntent.secret,
        locale: locale as any,
        appearance: {
          theme: 'stripe',
          variables: {
            colorBackground: '#fff',
          },
        },
      }}
    >
      <ElementsContextLogic
        isElementsReady={true}
        paymentIntent={paymentIntent}
        baseUrl={baseUrl}
      >
        {children}
      </ElementsContextLogic>
    </Elements>
  )
}

const ElementsContextLogic = ({
  isElementsReady,
  paymentIntent,
  baseUrl,
  children,
}: {
  isElementsReady: boolean
  paymentIntent: PaymentIntentData
  baseUrl: string
  children: ReactNode
}) => {
  const stripe = useStripe()
  const elements = useElements()

  const [message, setMessage] = useState<string | null | undefined>(null)
  const [isLoading, setIsLoading] = useState<boolean>(false)

  const [reservationId, setReservationId] = useState<string>('')

  useEffect(() => {
    const fetchUpdates = async () => {
      await elements?.fetchUpdates()
    }
    void fetchUpdates()
  }, [paymentIntent])

  useEffect(() => {
    if (!stripe) {
      return
    }

    const clientSecret = new URLSearchParams(window.location.search).get(
      'payment_intent_client_secret',
    )

    if (!clientSecret) {
      return
    }

    void stripe
      .retrievePaymentIntent(clientSecret)
      .then(({ paymentIntent }) => {
        switch (paymentIntent?.status) {
          case 'succeeded':
            setMessage('Payment succeeded!')
            break
          case 'processing':
            setMessage('Your payment is processing.')
            break
          case 'requires_payment_method':
            setMessage('Your payment was not successful, please try again.')
            break
          default:
            setMessage('Something went wrong.')
            break
        }
      })
  }, [stripe])

  const submitStripe = async () => {
    // to submit stripe
    if (!stripe || !elements) {
      // Stripe.js hasn't yet loaded.
      // Make sure to disable form submission until Stripe.js has loaded.
      return
    }

    setIsLoading(true)

    const { error } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: createReturnUrl({
          baseUrl,
          method: PaymentEnum.STRIPE,
          reservationId: reservationId,
        }),
      },
    })
    // This point will only be reached if there is an immediate error when
    // confirming the payment. Otherwise, your customer will be redirected to
    // your `return_url`. For some payment methods like iDEAL, your customer will
    // be redirected to an intermediate site first to authorize the payment, then
    // redirected to the `return_url`.
    if (error.type === 'card_error' || error.type === 'validation_error') {
      setMessage(error.message)
    } else {
      setMessage('An unexpected error occurred.')
    }

    setIsLoading(false)
  }

  // when we have the contactId submit stripe
  useEffect(() => {
    if (reservationId) {
      void submitStripe()
    }
  }, [reservationId])

  const handleStripeFromSubmit = async (fomData: FormData) => {
    const reservationResponse = await fetch('/api/reservation/create', {
      method: 'POST',
      body: fomData,
    })

    const response = (await reservationResponse.json()) as any
    if ('reservationId' in response) {
      setReservationId(response.reservationId)
    }
  }

  return (
    <ElementsContext.Provider
      value={{
        isElementsReady,
        stripe,
        elements,
        message,
        isLoading,
        handleStripeFromSubmit,
      }}
    >
      {children}
    </ElementsContext.Provider>
  )
}
